<template>
	<div>
		<v-data-table
			style="border-top: #e1dce8 1px solid"
			:loading="loading"
			:no-data-text="$t('no-data-available')"
			:loading-text="$t('loading')"
			:items="filteredAuditReport"
			item-key="medicalStaffId"
			sort-by="staffName"
			fixed-header
			hide-default-header
			single-expand
			:headers-length="2"
			dense
			:height="pageHeight(174)"
			:items-per-page="-1">
			<template #item="{ item, expand, isExpanded, index }">
				<tr
					@click="expand(!isExpanded)"
					class="text-left"
					:class="{ 'odd-row': index % 2, 'expanded-row': isExpanded }">
					<td>
						<v-icon small color="primary">
							{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
						</v-icon>
						<span>{{ item.staffName }}</span>
						<v-chip pill x-small color="primary" class="ml-2">{{ item.reports.length }} {{ $t('hospitals') }}</v-chip>
					</td>
					<td class="text-right">
						{{ formatDuration(item.totalBillingSeconds) }}
						<v-icon
							@click.stop="getDetailsByStaff(item.medicalStaffId, item.staffName)"
							small
							color="blue"
							class="ml-2">
							mdi-information-outline
						</v-icon>
					</td>
				</tr>
			</template>
			<template #expanded-item="{ item: staffGroup }">
				<tr style="cursor: default; background-color: #fff !important">
					<td class="pa-3" :colspan="2">
						<v-data-table
							class="data-table-outline"
							dense
							hide-default-header
							hide-default-footer
							single-expand
							item-key="uniqueKey"
							sort-by="hospitalName"
							:items-per-page="-1"
							:items="staffGroup.reports">
							<template #item="{ item, index, expand, isExpanded }">
								<tr
									@click="expand(!isExpanded)"
									class="text-left"
									:class="{ 'odd-row': index % 2, 'expanded-row': isExpanded }">
									<td class="text-left">
										<v-icon small color="primary">
											{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
										</v-icon>
										{{ item.hospitalName }}
										<v-chip x-small color="green lighten-2" class="ml-2">
											{{ getEntryCount(item.timeTrackings) }} {{ $t('trackable-activities') }}
										</v-chip>
										<v-chip x-small color="blue lighten-2" class="ml-2">
											{{ getEntryCount(item.medicalNotes) }} {{ $t('medical-notes') }}
										</v-chip>
									</td>
									<td class="text-right">
										{{ formatDuration(item.totalBillingSeconds) }}
										<v-icon
											@click.stop="
												getDetailsByHospital(item.medicalStaffId, item.hospitalId, item.staffName, item.hospitalName)
											"
											small
											color="blue"
											class="ml-2">
											mdi-information-outline
										</v-icon>
									</td>
								</tr>
							</template>
							<template #expanded-item="{ item: auditReport }">
								<tr style="cursor: default; background-color: #fff !important">
									<td :colspan="2" class="pa-3">
										<v-data-table
											class="data-table-outline"
											dense
											hide-default-header
											hide-default-footer
											single-expand
											:items-per-page="-1"
											:items="auditTypes">
											<template #item="{ item, index, expand, isExpanded }">
												<tr
													@click="expand(!isExpanded)"
													class="text-left"
													:class="{ 'odd-row': index % 2, 'expanded-row': isExpanded }">
													<td class="text-left">
														<v-icon small color="primary">
															{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
														</v-icon>
														{{ item.name }}
														<v-chip x-small color="primary" class="ml-2">
															{{ auditReport[item.id].length }}
															{{ auditReport[item.id].length !== 1 ? $t('patients') : $t('patient') }}
														</v-chip>
														<v-chip x-small color="primary" class="ml-2">
															{{ getEntryCount(auditReport[item.id]) }} {{ $t('entries') }}
														</v-chip>
													</td>
													<td class="text-right">
														{{ formatDuration(auditReport[item.seconds]) }}
													</td>
												</tr>
											</template>
											<template #expanded-item="{ item }">
												<tr style="cursor: default; background-color: #f9f9f9 !important">
													<td
														:class="['pa-0', { 'pb-3': auditTypes.indexOf(item) < auditTypes.length - 1 }]"
														:colspan="2">
														<v-data-table
															:no-data-text="$t('no-data-available')"
															:items="auditReport[item.id]"
															:headers="auditTypeHeaders[item.id]"
															fixed-header
															dense
															:items-per-page="-1"
															hide-default-footer>
															<template #item="{ item, index }">
																<tr :class="{ 'odd-row': index % 2 }">
																	<td class="text-left">{{ item.residentName }}</td>
																	<td class="text-center">
																		<v-chip x-small color="primary" class="ml-2">
																			{{ item.count }}
																		</v-chip>
																	</td>
																	<td class="text-right">
																		{{ formatDuration(item.totalBillingSeconds) }}
																		<v-icon
																			@click.stop="
																				getDetailsByPatient(
																					item.medicalStaffId,
																					item.hospitalId,
																					item.residentId,
																					item.staffName,
																					item.hospitalName,
																					item.residentName,
																				)
																			"
																			small
																			color="blue"
																			class="ml-2">
																			mdi-information-outline
																		</v-icon>
																	</td>
																</tr>
															</template>
														</v-data-table>
													</td>
												</tr>
											</template>
										</v-data-table>
									</td>
								</tr>
							</template>
						</v-data-table>
					</td>
				</tr>
			</template>
		</v-data-table>
		<v-dialog v-model="detailsDialog" v-if="reportDetails" persistent width="auto">
			<v-card style="width: 90vw; min-width: 750px; height: 90vh; min-height: 400px">
				<v-card-title class="card-header text-left fixed-card-title2">
					<v-row no-gutters class="d-flex justify-end align-center">
						{{ $t('audit-report') }}: {{ detailsFilter }}
						<v-spacer />
						<v-icon color="white" @click="detailsDialog = false">mdi-window-close</v-icon>
					</v-row>
				</v-card-title>
				<v-card-text class="pa-2 overflow-y-auto" style="height: calc(100% - 64px)">
					<v-row dense style="height: calc(100% + 8px)">
						<v-col cols="6" class="hp-100">
							<v-card class="hp-100">
								<v-card-title class="pa-2 text-body-2">
									<v-row no-gutters class="d-flex justify-end align-center">
										{{ $t('trackable-activities') }}
										<v-spacer />
										{{ formatDuration(getTotalTime(reportDetails.timeTrackingSummaryDto)) }}
									</v-row>
								</v-card-title>
								<v-card-text class="pa-2 pt-0" style="height: calc(100% - 36px)">
									<v-data-table
										:no-data-text="$t('no-data-available')"
										:items="reportDetails.timeTrackingSummaryDto"
										:headers="timeTrackingsDetailsHeaders"
										class="data-table-outline hp-100"
										fixed-header
										dense
										height="100%"
										:items-per-page="-1"
										hide-default-footer>
										<template #item="{ item, index }">
											<tr :class="{ 'odd-row': index % 2 }" class="text-left">
												<td class="px-2">{{ item.trackingId }}</td>
												<td class="px-2">{{ item.hospitalName }}</td>
												<td class="px-2">{{ item.residentFirstName }} {{ item.residentLastName }}</td>
												<td class="px-2">{{ formatterFilter(item.dateTime, 'datetime_med') }}</td>
												<td class="text-right px-2">{{ formatDuration(item.totalBillingSeconds) }}</td>
											</tr>
										</template>
									</v-data-table>
								</v-card-text>
							</v-card>
						</v-col>
						<v-col cols="6" class="hp-100">
							<v-card class="hp-100">
								<v-card-title class="pa-2 text-body-2">
									<v-row no-gutters class="d-flex justify-end align-center">
										{{ $t('medical-notes') }}
										<v-spacer />
										{{ formatDuration(getTotalTime(reportDetails.medicalNoteSummaryDto)) }}
									</v-row>
								</v-card-title>
								<v-card-text class="pa-2 pt-0" style="height: calc(100% - 36px)">
									<v-data-table
										:no-data-text="$t('no-data-available')"
										:items="reportDetails.medicalNoteSummaryDto"
										:headers="medicalNotesDetailsHeaders"
										class="data-table-outline hp-100"
										fixed-header
										dense
										:items-per-page="-1"
										hide-default-footer
										height="100%">
										<template #item="{ item, index }">
											<tr :class="{ 'odd-row': index % 2 }" class="text-left">
												<td class="px-2">{{ item.hospitalName }}</td>
												<td class="px-2">{{ item.residentFirstName }} {{ item.residentLastName }}</td>
												<td class="px-2">{{ formatterFilter(item.dateTime, 'datetime_med') }}</td>
												<td class="text-right px-2">{{ formatDuration(item.totalBillingSeconds) }}</td>
											</tr>
										</template>
									</v-data-table>
								</v-card-text>
							</v-card>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { formatDuration } from '@/utils/utilities';
import { formatterFilter } from '@/utils/luxon-formater';

export default {
	name: 'AuditComponent',
	data () {
		return {
			loading: true,
			detailsDialog: false,
			detailsFilter: '',
		};
	},
	computed: {
		...mapState({
			auditReport: (state) => state.audits.report,
			reportDetails: (state) => state.audits.reportDetails,
			userData: (state) => state.authentication.userData,
		}),
		...mapGetters({
			appBarSearch: 'filterbar/getSearch',
			selectedMonthAndYear: 'filterbar/getBillingDate',
			pageHeight: 'pageHeight',
		}),
		timeTrackingsHeaders () {
			return [
				{ text: this.$t('patient-name'), value: 'residentName' },
				{ text: this.$t('trackable-activities'), value: 'count', align: 'center', sortable: false },
				{ text: this.$t('total-billing-time'), value: 'totalBillingTime', align: 'right' },
			];
		},
		medicalNotesHeaders () {
			return [
				{ text: this.$t('patient-name'), value: 'residentName' },
				{ text: this.$t('count'), value: 'count', align: 'center', sortable: false },
				{ text: this.$t('total-billing-time'), value: 'totalBillingTime', align: 'right' },
			];
		},
		auditTypeHeaders () {
			return {
				timeTrackings: this.timeTrackingsHeaders,
				medicalNotes: this.medicalNotesHeaders,
			};
		},
		auditTypes () {
			return [
				{ name: this.$t('trackable-activities'), id: 'timeTrackings', seconds: 'totalTimeTrackingsSeconds' },
				{ name: this.$t('medical-notes'), id: 'medicalNotes', seconds: 'totalMedicalNotesSeconds' },
			];
		},
		timeTrackingsDetailsHeaders () {
			return [
				{ text: this.$t('tracking-id'), value: 'trackingId', class: 'px-2' },
				{ text: this.$t('hospital-name'), value: 'hospitalName', class: 'px-2' },
				{ text: this.$t('patient-name'), value: 'residentFirstName', class: 'px-2' },
				{ text: this.$t('log-time'), value: 'dateAdded', class: 'px-2' },
				{ text: this.$t('total-billing-time'), value: 'totalBillingSeconds', align: 'right', class: 'px-2' },
			];
		},
		medicalNotesDetailsHeaders () {
			return [
				{ text: this.$t('hospital-name'), value: 'hospitalName', class: 'px-2' },
				{ text: this.$t('patient-name'), value: 'residentFirstName', class: 'px-2' },
				{ text: this.$t('log-time'), value: 'dateAdded', class: 'px-2' },
				{ text: this.$t('total-billing-time'), value: 'totalBillingSeconds', align: 'right', class: 'px-2' },
			];
		},
		groupedAuditReport () {
			return this.auditReport.reduce((acc, report) => {
				const { medicalStaffId, medicalStaffFirstName, medicalStaffLastName, ...rest } = report;
				let grp = acc.find((g) => g.medicalStaffId === medicalStaffId);
				if (!grp) {
					grp = {
						staffName: medicalStaffFirstName + ' ' + medicalStaffLastName,
						medicalStaffId,
						totalBillingSeconds: 0,
						reports: [],
					};
					acc.push(grp);
				}
				const totalTimeTrackingsSeconds = report.timeTrackings.reduce((result, t) => {
					return result + t.totalBillingSeconds;
				}, 0);
				const totalMedicalNotesSeconds = report.medicalNotes.reduce((result, m) => {
					return result + m.totalBillingSeconds;
				}, 0);
				const totalBillingSeconds = totalTimeTrackingsSeconds + totalMedicalNotesSeconds;
				grp.totalBillingSeconds += totalBillingSeconds;
				grp.reports.push({
					...rest,
					staffName: grp.staffName,
					medicalStaffId,
					uniqueKey: `${report.hospitalId}-${medicalStaffId}`,
					totalTimeTrackingsSeconds,
					totalMedicalNotesSeconds,
					totalBillingSeconds,
					timeTrackings: report.timeTrackings.map((t) => ({
						...t,
						medicalStaffId,
						hospitalId: report.hospitalId,
						staffName: grp.staffName,
						hospitalName: report.hospitalName,
						count: t.totalTimeTrackings,
						residentName: t.residentFirstName + ' ' + t.residentLastName,
					})),
					medicalNotes: report.medicalNotes.map((n) => ({
						...n,
						medicalStaffId,
						hospitalId: report.hospitalId,
						staffName: grp.staffName,
						hospitalName: report.hospitalName,
						count: n.totalNotes,
						residentName: n.residentFirstName + ' ' + n.residentLastName,
					})),
				});
				return acc;
			}, []);
		},
		filteredAuditReport () {
			const search = this.appBarSearch?.toLowerCase().trim();
			if (!search) return this.groupedAuditReport;

			const result = [];
			for (const staffGroup of this.groupedAuditReport) {
				if (staffGroup.staffName.toLowerCase().includes(search)) {
					result.push(staffGroup);
					continue;
				}

				const filteredReports = [];
				for (const report of staffGroup.reports) {
					if (report.hospitalName.toLowerCase().includes(search)) {
						filteredReports.push(report);
						continue;
					}

					const filteredTimeTrackings = report.timeTrackings.filter((t) =>
						t.residentName.toLowerCase().includes(search),
					);
					const filteredMedicalNotes = report.medicalNotes.filter((n) => n.residentName.toLowerCase().includes(search));

					if (filteredTimeTrackings.length || filteredMedicalNotes.length) {
						filteredReports.push({
							...report,
							timeTrackings: filteredTimeTrackings,
							medicalNotes: filteredMedicalNotes,
						});
					}
				}

				if (filteredReports.length) {
					result.push({ ...staffGroup, reports: filteredReports });
				}
			}
			return result;
		},
	},
	mounted () {
		this.getReport();
	},
	methods: {
		...mapActions({
			getAuditReport: 'audits/getAuditReport',
			getByMedicalStaffId: 'audits/getByMedicalStaffId',
			getByMedicalStaffIdByHospitalId: 'audits/getByMedicalStaffIdByHospitalId',
			getByMedicalStaffIdByHospitalIdByPatientId: 'audits/getByMedicalStaffIdByHospitalIdByPatientId',
		}),
		formatDuration,
		formatterFilter,
		async getReport () {
			let byYear, byMonth;
			if (this.selectedMonthAndYear) {
				[byYear, byMonth] = this.selectedMonthAndYear.split('-');
			} else {
				const today = new Date();
				byYear = today.getFullYear();
				byMonth = String(today.getMonth() + 1).padStart(2, '0');
			}
			await this.getAuditReport({ organizationId: this.userData.organizationId, byMonth, byYear });
			this.loading = false;
		},
		async getDetailsByStaff (staffId, staffName) {
			this.loading = true;
			this.detailsFilter = staffName;
			let byYear, byMonth;
			if (this.selectedMonthAndYear) {
				[byYear, byMonth] = this.selectedMonthAndYear.split('-');
			} else {
				const today = new Date();
				byYear = today.getFullYear();
				byMonth = String(today.getMonth() + 1).padStart(2, '0');
			}
			await this.getByMedicalStaffId({ staffId, byMonth, byYear });
			this.detailsDialog = true;
			this.loading = false;
		},
		async getDetailsByHospital (staffId, hospitalId, staffName, hospitalName) {
			this.loading = true;
			this.detailsFilter = `${staffName}, ${hospitalName}`;
			let byYear, byMonth;
			if (this.selectedMonthAndYear) {
				[byYear, byMonth] = this.selectedMonthAndYear.split('-');
			} else {
				const today = new Date();
				byYear = today.getFullYear();
				byMonth = String(today.getMonth() + 1).padStart(2, '0');
			}
			await this.getByMedicalStaffIdByHospitalId({ staffId, hospitalId, byMonth, byYear });
			this.detailsDialog = true;
			this.loading = false;
		},
		async getDetailsByPatient (staffId, hospitalId, patientId, staffName, hospitalName, residentName) {
			this.loading = true;
			this.detailsFilter = `${staffName}, ${hospitalName}, ${residentName}`;
			let byYear, byMonth;
			if (this.selectedMonthAndYear) {
				[byYear, byMonth] = this.selectedMonthAndYear.split('-');
			} else {
				const today = new Date();
				byYear = today.getFullYear();
				byMonth = String(today.getMonth() + 1).padStart(2, '0');
			}
			await this.getByMedicalStaffIdByHospitalIdByPatientId({ staffId, hospitalId, patientId, byMonth, byYear });
			this.detailsDialog = true;
			this.loading = false;
		},
		getTotalTime (list) {
			return list.reduce((result, m) => {
				return result + m.totalBillingSeconds;
			}, 0);
		},
		getEntryCount (item) {
			return item.reduce((result, m) => {
				return result + m.count;
			}, 0);
		},
	},
	watch: {
		async selectedMonthAndYear (val) {
			if (val) {
				this.loading = true;
				const [byYear, byMonth] = this.selectedMonthAndYear.split('-');
				await this.getAuditReport({ organizationId: this.userData.organizationId, byMonth, byYear });
				this.loading = false;
			}
		},
	},
};
</script>
